import { isEqual } from 'lodash'
import React, { ReactElement, useEffect, useRef, useState } from 'react'
import Close from '../icons/Close'
import { RADOM_COLORS } from '../util/Constants'
import { ManagedPaymentMethod, getPaymentMethods } from '../util/Managed'
import { IconButton } from './Button'
import Checkbox from './Checkbox'
import Dropdown, { DropdownContentHeader, DropdownItem, DropdownTextInput } from './Dropdown'
import { InputLabel } from './Input'
import { SelectedMethodIcon } from './PaymentMethodDisplay'
import Spinner from './Spinner'
import Switch from './Switch'
import Tooltip from './Tooltip'
import User from '../state/User'

interface IProps {
  disabled?: boolean
  header?: ReactElement
  placeholderText?: string
  selectedMethods: ManagedPaymentMethod[]
  setSelectedMethods: React.Dispatch<React.SetStateAction<ManagedPaymentMethod[]>>
  isLoading?: boolean
  allowTestnets?: boolean
  testnetOnly?: boolean
  label?: string
  allowedNetworks?: string[]
  filterMethod?: (method: ManagedPaymentMethod) => boolean
  getMethodAvailability?: (method: ManagedPaymentMethod) => { available: boolean, reason?: string }
  hideLabel?: boolean
  hideDisabled?: boolean
  height?: number
  showLabel?: boolean
  hideCheckbox?: boolean
}

export const ManagedPaymentMethodSelector = (props: IProps): ReactElement => {
  const [query, setQuery] = useState('')
  const dropdownRef = useRef<Dropdown>(null)
  const queryInputRef = useRef<HTMLInputElement>(null)
  const [shouldHideMonero, setShouldHideMonero] = useState(true)

  const setDropdown = (open: boolean): void => {
    dropdownRef.current?.setState({ open })
  }

  const onQueryChange = (e: React.ChangeEvent): void => {
    const val = (e.target as HTMLInputElement).value
    if (val !== '') {
      setDropdown(true)
    }
    setQuery(val)
  }

  useEffect(() => {
    User.getOrganization()
      .then((o) => setShouldHideMonero(!o.features?.isMoneroEnabled))
      .catch(() => setShouldHideMonero(true))
  }, [])

  const [testnetMode, setTestnetMode] = useState(false)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%', fontSize: 14 }}>
      {!props.hideLabel && <InputLabel>{props.label ?? 'Payment methods'}</InputLabel>}
      <Dropdown
        ref={dropdownRef}
        disabled={props.disabled}
        preferredAlignment='right'
        selectedContent={
          <>
            { props.isLoading && <Spinner /> }
            {
              props.selectedMethods.length === 0 &&
              <div style={{ display: 'flex', alignItems: 'center', minHeight: props.height ?? 'auto', color: RADOM_COLORS.GRAY_DARKER, fontSize: 13.5 }}>
                {props.placeholderText ?? 'Select methods'}
              </div>
            }
            {
              !props.isLoading && props.selectedMethods.length > 0 &&
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap'
              }}>
                {
                  props.selectedMethods.map((method, i) =>
                    <div key={i} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      minHeight: props.height ?? 'auto'
                    }}>
                      <SelectedMethodIcon method={method} />
                      {props.showLabel && <>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <span style={{ color: RADOM_COLORS.BLACK, fontSize: 13.5, fontWeight: 500, lineHeight: 1.2 }}>
                            {method.ticker}
                          </span>
                          <span style={{ color: RADOM_COLORS.GRAY_DARKEST, fontSize: 11.5, lineHeight: 1.2 }}>
                            {method.hostChain ? `${method.name} on ${method.hostChain.name}` : method.name}
                          </span>
                        </div>
                      </>
                      }
                    </div>
                  )
                }
              </div>
            }
          </>
        }
        dropdownContent={
          <div>
            <DropdownContentHeader style={{
              borderBottom: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
              zIndex: 1,
              position: 'sticky',
              top: 0,
              background: 'white'
            }}>
              <DropdownTextInput
                autoFocus
                ref={queryInputRef}
                type="text"
                value={query}
                onChange={onQueryChange}
                placeholder={'Search...'}
              />
              {
                query !== '' &&
                  <IconButton
                    style={{ position: 'absolute', right: 10, padding: 5 }}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setQuery('')
                    }}>
                    <Close style={{ width: 10, pointerEvents: 'none' }} />
                  </IconButton>
              }
            </DropdownContentHeader>

            <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' }}>
              <div style={{ overflow: 'scroll' }}>
                {getPaymentMethods()
                  .filter(m => {
                    if (m.name !== 'Monero' && m.name !== 'MoneroStagenet') return true
                    return !shouldHideMonero
                  })
                  .filter(m => !m.isHidden)
                  .filter(m => props.filterMethod === undefined || props.filterMethod(m))
                  .filter(m => {
                    if (!props.hideDisabled) {
                      return true
                    }

                    let disabled = m.isDisabled
                    if (props.allowedNetworks && props.allowedNetworks.length > 0) {
                      const networkName = m.hostChain?.name || m.name
                      if (!props.allowedNetworks.includes(networkName)) {
                        disabled = true
                      }
                    }

                    return !disabled
                  })
                  .filter(m => {
                    if (!query) return true
                    const lowercaseQuery = query.toLowerCase()
                    return m.name.toLowerCase().includes(lowercaseQuery) ||
                          m.ticker.toLowerCase().includes(lowercaseQuery) ||
                          m.hostChain?.name.toLowerCase().includes(lowercaseQuery)
                  })
                  .filter(t => (testnetMode || props.testnetOnly) ? t.isTestnet : !t.isTestnet)
                  .map((p, i) => {
                    const availability = props.getMethodAvailability?.(p)
                    const unavailable = !!availability && !availability.available

                    let disabled = p.isDisabled || unavailable
                    if (props.allowedNetworks && props.allowedNetworks.length > 0) {
                      const networkName = p.hostChain?.name || p.name
                      if (!props.allowedNetworks.includes(networkName)) {
                        disabled = true
                      }
                    }

                    return <DropdownItem
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        minHeight: 52,
                        fontSize: 14,
                        opacity: disabled ? 0.25 : 1
                      }}
                      onClick={e => {
                        if (disabled) {
                          return
                        }

                        if (props.selectedMethods.find(c => isEqual(c, p))) {
                          props.setSelectedMethods(props.selectedMethods.filter(c => !isEqual(c, p)))
                        } else {
                          props.setSelectedMethods([...new Set([...props.selectedMethods, p])])
                        }
                        if (props.hideCheckbox) {
                          setDropdown(false)
                        }

                        e.stopPropagation()
                      }}

                    >
                      {!props.hideCheckbox &&
                      <Checkbox size={16} checked={!!props.selectedMethods.find(cc => isEqual(cc, p))} />}
                      <div style={{
                        width: 25,
                        height: 25,
                        backgroundImage: `url(${p.logo})`,
                        backgroundSize: 'contain',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        position: 'relative'
                      }}>
                        {
                          p.hostChain && <div style={{
                            background: 'rgba(255, 255, 255, 0.95)',
                            borderRadius: '100%',
                            width: 15,
                            height: 15,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'absolute',
                            bottom: -1,
                            right: -5,
                            boxShadow: '0 0 1px'
                          }}>
                            <div style={{
                              width: 10,
                              height: 10,
                              backgroundImage: `url(${p.hostChain.logo})`,
                              backgroundSize: 'contain',
                              backgroundPosition: 'center',
                              backgroundRepeat: 'no-repeat'
                            }} />
                          </div>
                        }
                      </div>
                      <div style={{ display: 'flex', minWidth: 0, flexDirection: 'column', gap: 2 }}>
                        <span style={{ color: RADOM_COLORS.BLACK, lineHeight: 1.25, fontWeight: 500 }}>{p.ticker}</span>
                        <span style={{ color: RADOM_COLORS.GRAY_DARKEST, fontSize: 12, lineHeight: 1.2 }}>
                          {p.name}{p.hostChain ? ` on ${p.hostChain.name}` : ''}
                        </span>
                        {p.isDisabled && <span style={{
                          background: RADOM_COLORS.GRAY_DARK,
                          color: 'black',
                          textTransform: 'uppercase',
                          fontSize: 10,
                          padding: '2px 8px',
                          borderRadius: 5
                        }}>Coming Soon</span>}
                        {!p.isDisabled && unavailable && <span style={{
                          alignSelf: 'flex-start',
                          background: RADOM_COLORS.GRAY_DARK,
                          color: RADOM_COLORS.GRAY_DARKEST,
                          textTransform: 'uppercase',
                          fontSize: 10,
                          padding: '2px 8px',
                          borderRadius: 5
                        }}>{availability?.reason ?? 'Temporarily unavailable'}</span>}
                      </div>
                    </DropdownItem>
                  })}
              </div>
            </div>

            {
              !props.testnetOnly && props.allowTestnets &&
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                position: 'sticky',
                bottom: 0,
                background: '#fff',
                borderTop: '1px solid #edf1f7'
              }}>
                <div
                  onClick={() => {
                    setTestnetMode(!testnetMode)
                    props.setSelectedMethods([])
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 5,
                    padding: 10,
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}>
                  <Switch checked={testnetMode} sizeScale={0.5} />
                  <span style={{
                    color: RADOM_COLORS.GRAY_DARKER,
                    fontSize: 12,
                    textTransform: 'uppercase'
                  }}>Testnet mode</span>
                  <Tooltip message='Use testnets to validate your checkout before moving to production.' />
                </div>
              </div>
            }
          </div>
        }
      />
    </div>
  )
}
